import { useEffect } from 'react';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';
import { VenueReadiness, type VenueReadinessView } from '../VenueReadiness.tsx';

/**
 * What the cloud can say about this event's venue authority right now.
 *
 * Polled rather than pushed, and deliberately so: freshness is a property of
 * the *server's* knowledge, and the only way to learn that it has changed is to
 * ask again. A page that computed staleness locally would appear to update
 * while learning nothing.
 *
 * Everything rendered comes from the projection. The component below holds no
 * threshold, no mode inference, and no arithmetic over cursors.
 */
const POLL_MILLISECONDS = 1_000;

export function EventVenueAuthorityPage() {
  const { event, organizationId } = useEventWorkspace();
  const { resource, reload } = useResource<VenueReadinessView>(
    `/api/v1/organizer/organizations/${organizationId}/events/${event.eventId}/venue-readiness`,
  );

  useEffect(() => {
    const timer = setInterval(reload, POLL_MILLISECONDS);
    return () => {
      clearInterval(timer);
    };
  }, [reload]);

  if (resource.status === 'loading') return <p aria-busy="true">Loading venue authority…</p>;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return <VenueReadiness view={resource.data} />;
}
